import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findSkin, findTheme } from '@snakefeast/shared';
import { api } from '../lib/api';
import { enablePush, pushState, type PushState } from '../lib/push';
import { sfx } from '../lib/sound';
import { useSession } from '../store/session';
import { toast } from '../store/toasts';
import { SkinPreview, ThemePreview } from './SkinPreview';
import { Glyph } from './GameIcon';

export interface MissionsState {
  day: string;
  missions: Array<{ id: string; icon: string; target: number; progress: number; done: boolean }>;
  allDone: boolean;
  justCompleted: boolean;
  completedDays: number;
  streak: number;
  rewards: Array<{ days: number; kind: 'skin' | 'theme'; id: string; unlocked: boolean }>;
  dayReward: { coins: number; xp: number } | null;
}

/** Today's three missions, the streak, the next reward, and a one-tap "remind me" for web push. */
export function MissionsCard() {
  const { t } = useTranslation();
  // undefined while loading, null if it failed (the card is then left out).
  const [state, setState] = useState<MissionsState | null | undefined>(undefined);
  const [push, setPush] = useState<PushState>('unsupported');

  useEffect(() => {
    let cancelled = false;
    void api<MissionsState>('/api/missions')
      .then((s) => {
        if (cancelled) return;
        setState(s);
        if (s.justCompleted) {
          sfx('newBest');
          toast(`🎉 ${t('missions.allDone')}${s.dayReward ? ` ${t('missions.dayReward', s.dayReward)}` : ''}`, 'success');
          if (s.dayReward) void useSession.getState().reloadMe().catch(() => {});
        }
      })
      .catch(() => !cancelled && setState(null));
    void pushState().then((p) => !cancelled && setPush(p));
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (state === null) return null;
  if (!state) {
    return (
      <section className="card missions" aria-busy="true" aria-label={t('missions.title')}>
        <div className="skeleton" style={{ width: 150, height: 20 }} />
        <ul className="mission-list">
          {[62, 48, 55].map((w, i) => (
            <li key={i}>
              <div className="skeleton" style={{ width: 24, height: 24, borderRadius: 6, flex: 'none' }} />
              <div className="grow">
                <div className="row">
                  <div className="skeleton grow" style={{ maxWidth: `${w}%`, height: 14 }} />
                  <span className="grow" />
                  <div className="skeleton" style={{ width: 30, height: 12 }} />
                </div>
                <div className="skeleton" style={{ height: 6, borderRadius: 999, marginTop: 6 }} />
              </div>
            </li>
          ))}
        </ul>
        <div className="row" style={{ marginTop: 14 }}>
          <div className="skeleton" style={{ width: 40, height: 40, flex: 'none' }} />
          <div className="skeleton" style={{ width: 240, maxWidth: '70%', height: 12 }} />
        </div>
      </section>
    );
  }
  const next = state.rewards.find((r) => !r.unlocked);
  const remind = async () => {
    try {
      const p = await enablePush();
      setPush(p);
      if (p === 'on') toast(t('missions.remindersOn'), 'success');
    } catch {
      toast(t('errors.network'), 'error');
    }
  };

  return (
    <section className="card missions" aria-label={t('missions.title')}>
      <div className="row missions-head">
        <h3 className="grow"><Glyph e="🎯" /> {t('missions.title')}</h3>
        {state.streak > 0 && <span className="pill"><Glyph e="🔥" /> {t('missions.streak', { count: state.streak })}</span>}
      </div>
      <ul className="mission-list">
        {state.missions.map((m) => (
          <li key={m.id} className={m.done ? 'done' : ''}>
            <span className="mission-icon"><Glyph e={m.done ? '✅' : m.icon} size={26} /></span>
            <div className="grow">
              <div className="row">
                <span className="grow">{t(`missions.items.${m.id}`, { count: m.target })}</span>
                <small className="faint">{m.progress}/{m.target}</small>
              </div>
              <div className="mission-bar" role="progressbar" aria-valuemin={0} aria-valuemax={m.target} aria-valuenow={m.progress}>
                <i style={{ width: `${(m.progress / m.target) * 100}%` }} />
              </div>
            </div>
          </li>
        ))}
      </ul>
      {next && (
        <div className="mission-reward">
          {next.kind === 'skin' ? (
            <span className="swatch small"><SkinPreview skin={findSkin(next.id)} /></span>
          ) : (
            <span className="swatch small theme-swatch"><ThemePreview theme={findTheme(next.id)} size={40} /></span>
          )}
          <span className="faint">
            {state.allDone ? <><Glyph e="✅" /> {t('missions.doneToday')} · </> : ''}
            {t('missions.nextReward', { count: next.days - state.completedDays, item: t(`missions.rewards.${next.id}`) })}
          </span>
        </div>
      )}
      {!next && <div className="faint">{t('missions.allRewards')}</div>}
      {push === 'off' && (
        <button className="btn small block mt" onClick={() => void remind()}><Glyph e="🔔" /> {t('missions.remindMe')}</button>
      )}
    </section>
  );
}
